import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Save, Loader2, Plus, Trash2, ArrowRight, GitBranch } from 'lucide-react';
import { flattenInputFields } from '../lib/formUtils';
import { getNextStepIds } from '../lib/workflowEdgeUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPERATORS = [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'not equals' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'not contains' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
    { value: 'greater_equal', label: 'greater or equal' },
    { value: 'less_equal', label: 'less or equal' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_true', label: 'is true' },
    { value: 'is_false', label: 'is false' },
    { value: 'in_list', label: 'in list' },
    { value: 'not_in_list', label: 'not in list' },
    { value: 'exists', label: 'exists' },
    { value: 'not_exists', label: 'not exists' },
];

// Operators that don't take a value input (unary operators).
// These render without a value text field in the condition editor.
const UNARY_OPS = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false', 'exists', 'not_exists']);

// ─── Match Toggle (All/Any) ──────────────────────────────────────────────────
function MatchToggle({ value, onChange }) {
    return (
        <div style={{ display: 'flex', gap: 4 }}>
            {['all', 'any'].map(opt => (
                <button
                    key={opt}
                    type="button"
                    onClick={() => onChange(opt)}
                    style={{
                        padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                        background: value === opt ? '#1C1917' : '#F1F0EB',
                        color: value === opt ? '#FFFFFF' : '#6E6961',
                    }}
                >
                    {opt}
                </button>
            ))}
        </div>
    );
}

// ─── Label a step by its kind for dropdowns ──────────────────────────────────
function stepLabel(s) {
    if (s.branch) return `Condition: ${s.title || 'Untitled'}`;
    if (s.kind === 'splash') return `Custom Page: ${s.title || 'Untitled'}`;
    if (s.kind === 'end_page') return `End Page: ${s.title || 'Untitled'}`;
    return `Form Page: ${s.title || 'Untitled'}`;
}

export default function ConditionEditor() {
    const { formId, nodeId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState('switch_case'); // switch_case | if_else
    const [sourceNode, setSourceNode] = useState(''); // branch.source_node (step_id)
    const [sourceField, setSourceField] = useState('');
    const [cases, setCases] = useState([]); // [{value, targetStepId}]
    const [branches, setBranches] = useState([]); // [{name, matchLogic, conditions: [{field, operator, value}], targetStepId}]
    const [defaultTarget, setDefaultTarget] = useState('');

    // ── Load form ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!formId || formId === 'new') { setLoading(false); return; }
        axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() })
            .then(res => {
                const f = res.data;
                setForm(f);
                // Find the step that has this nodeId as its step_id
                const steps = f.steps || [];
                const step = steps.find(s => s.step_id === nodeId);
                if (step && step.branch) {
                    const b = step.branch;
                    setMode(b.mode || 'switch_case');
                    setSourceNode(b.source_node || '');
                    setSourceField(b.source_field || '');
                    setCases(b.cases || []);
                    setBranches(b.branches || []);
                    setDefaultTarget(b.default_target || '');
                }
            })
            .catch(() => { toast.error('Form not found'); navigate(`/forms/${formId}/workflow`); })
            .finally(() => setLoading(false));
    }, [formId, nodeId, getAuthHeader, navigate]);

    // ── Collect all steps as potential targets ────────────────────────────
    // Fixed: label steps correctly by kind (was only distinguishing splash).
    const allSteps = useMemo(() => {
        if (!form?.steps) return [];
        // Include both steps AND forwarders as valid branch targets.
        // Forwarders are canvas nodes with step_id like "fwd_*" — they can be
        // the target of a condition branch (e.g., "if role=CEO → fire this
        // forwarder"). Without including them here, users can't select a
        // forwarder as a branch target in the ConditionEditor UI.
        const stepOptions = form.steps
            .filter(s => s.step_id !== nodeId) // can't branch to self
            .map((s, i) => ({
                step_id: s.step_id,
                title: s.title || `Step ${i + 1}`,
                kind: s.kind,
                label: stepLabel(s),
            }));
        const forwarderOptions = (form.forwarders || [])
            .filter(f => f.step_id !== nodeId) // can't branch to self
            .map(f => ({
                step_id: f.step_id,
                title: f.name || f.step_id,
                kind: 'forwarder',
                label: `Forwarder: ${f.name || f.step_id}`,
            }));
        return [...stepOptions, ...forwarderOptions];
    }, [form, nodeId]);

    // ── Get fields for a specific step (recursively flattened inputs) ────
    // Uses the shared flattenInputFields helper — no more top-level-only iteration.
    const getStepFields = useCallback((stepId) => {
        if (!form?.steps || !stepId) return [];
        const step = form.steps.find(s => s.step_id === stepId);
        if (!step || step.kind !== 'question') return [];
        return flattenInputFields(step.fields || []).map(f => ({
            field_id: f.field_id,
            label: f.label || f.field_id,
        }));
    }, [form]);

    // ── Fields for the selected From Node (switch_case source field dropdown) ─
    const sourceNodeFields = useMemo(() => getStepFields(sourceNode), [getStepFields, sourceNode]);

    // ── Save ──────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        try {
            const updatedSteps = (form.steps || []).map(s => {
                if (s.step_id === nodeId) {
                    return {
                        ...s,
                        branch: {
                            mode,
                            source_node: sourceNode || undefined,
                            source_field: sourceField,
                            cases: mode === 'switch_case' ? cases : [],
                            branches: mode === 'if_else' ? branches : [],
                            default_target: defaultTarget,
                        },
                    };
                }
                return s;
            });

            // source_node is a DATA DEPENDENCY ("which form page's answers to evaluate"),
            // NOT a flow edge. Do NOT set next_step_id on the source step.
            // The condition's incoming flow edge comes from the preceding step's
            // next_step_id, which the user draws manually on the canvas.
            //
            // Previously, this code overwrote the source step's next_step_id to
            // point to the condition, destroying the sequential chain (e.g.,
            // form1→form2 became form1→conditional, skipping form2/form3/custom).

            // Warn if the condition has no incoming edge (unreachable at runtime)
            // MULTI-EDGE: next_step_id can be a string or array; use shared utility.
            const hasIncomingEdge = (form.steps || []).some(s => {
                const ids = getNextStepIds(s);
                return ids.includes(nodeId);
            }) || (form.forwarders || []).some(f => {
                const ids = getNextStepIds(f);
                return ids.includes(nodeId);
            });
            if (!hasIncomingEdge) {
                console.warn('[ConditionEditor] No step has next_step_id pointing to this condition. The condition will be unreachable at runtime until the user draws an edge to it.');
            }

            const payload = { ...form, steps: updatedSteps };
            const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
            setForm(res.data);
            toast.success('Condition saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    // ── Switch Case operations ─────────────────────────────────────────────
    const addCase = () => {
        setCases([...cases, { value: '', targetStepId: '' }]);
    };
    const updateCase = (idx, field, value) => {
        setCases(cases.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    };
    const removeCase = (idx) => {
        setCases(cases.filter((_, i) => i !== idx));
    };

    // ── If/Else branch operations ──────────────────────────────────────────
    const addBranch = () => {
        setBranches([...branches, {
            name: `Branch ${branches.length + 1}`,
            matchLogic: 'all',
            conditions: [{ field: '', operator: 'equals', value: '' }],
            targetStepId: '',
        }]);
    };
    const updateBranch = (idx, field, value) => {
        setBranches(branches.map((b, i) => i === idx ? { ...b, [field]: value } : b));
    };
    const addCondition = (bIdx) => {
        setBranches(branches.map((b, i) => {
            if (i !== bIdx) return b;
            return { ...b, conditions: [...b.conditions, { field: '', operator: 'equals', value: '' }] };
        }));
    };
    const updateCondition = (bIdx, cIdx, field, value) => {
        setBranches(branches.map((b, i) => {
            if (i !== bIdx) return b;
            const newConditions = b.conditions.map((c, j) => {
                if (j !== cIdx) return c;
                const updated = { ...c, [field]: value };
                // Clear value when switching to a unary operator
                if (field === 'operator' && UNARY_OPS.has(value)) {
                    updated.value = '';
                }
                return updated;
            });
            return { ...b, conditions: newConditions };
        }));
    };
    const removeCondition = (bIdx, cIdx) => {
        setBranches(branches.map((b, i) => {
            if (i !== bIdx) return b;
            return { ...b, conditions: b.conditions.filter((_, j) => j !== cIdx) };
        }));
    };
    const removeBranch = (idx) => {
        setBranches(branches.filter((_, i) => i !== idx));
    };

    // ── Handle From Node change: reset stale field selections ──────────────
    const handleSourceNodeChange = (newNodeId) => {
        setSourceNode(newNodeId);
        const validFieldIds = new Set(getStepFields(newNodeId).map(f => f.field_id));

        // Clear source_field if it doesn't belong to the new node
        if (sourceField && !validFieldIds.has(sourceField)) {
            setSourceField('');
        }

        // Clear condition field values that don't belong to the new node
        setBranches(prev => prev.map(b => ({
            ...b,
            conditions: b.conditions.map(c => {
                if (c.field && !validFieldIds.has(c.field)) {
                    return { ...c, field: '' };
                }
                return c;
            }),
        })));
    };

    if (loading) {
        return (
            <div className="flex flex-1 items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    const inputClass = 'w-full px-2.5 py-2 border border-stone-200 rounded-md text-sm text-stone-900 bg-white focus:outline-none focus:border-stone-900';
    const labelClass = 'block text-[11px] font-semibold text-stone-600 mb-1.5 uppercase tracking-wide';

    return (
        <>
        <style>{`
            /* Fills the shell's full-height flex column for this route — see RuntimeShell. */
            .ce-page { flex: 1; min-height: 0; display: flex; flex-direction: column; background: #F7F6F2; }
            .ce-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; border-bottom: 1px solid #E8E5DF; background: #FFFFFF; flex-shrink: 0; gap: 12px; }
            .ce-header-left { display: flex; align-items: center; gap: 12px; }
            .ce-header h2 { font-size: 16px; font-weight: 700; color: #1C1917; }
            .ce-header-sub { font-size: 12px; color: #A29D96; }
            .ce-body { flex: 1; overflow-y: auto; padding: 24px; max-width: 800px; margin: 0 auto; width: 100%; }
            .ce-section { background: #FFFFFF; border: 1px solid #E8E5DF; border-radius: 8px; padding: 20px; margin-bottom: 16px; }
            .ce-section-title { font-size: 13px; font-weight: 700; color: #1C1917; margin-bottom: 12px; }
            .ce-mode-toggle { display: flex; gap: 0; border: 1px solid #E8E5DF; border-radius: 8px; overflow: hidden; margin-bottom: 16px; }
            .ce-mode-btn { flex: 1; padding: 10px 16px; border: none; background: #FFFFFF; font-size: 13px; font-weight: 600; color: #6E6961; cursor: pointer; transition: all 0.15s ease; }
            .ce-mode-btn.active { background: #1C1917; color: #FFFFFF; }
            .ce-case-row { display: flex; align-items: center; gap: 10px; padding: 10px; background: #F7F6F2; border-radius: 8px; margin-bottom: 8px; }
            .ce-case-row.default-case { background: #FCEFDD; border: 1px solid #F0D9B5; }
            .ce-case-number { width: 24px; height: 24px; border-radius: 50%; background: #B45309; color: #FFFFFF; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
            .ce-case-number.default-badge { background: #B45309; }
            .ce-branch { background: #FFFFFF; border: 1px solid #E8E5DF; border-radius: 8px; margin-bottom: 12px; overflow: hidden; }
            .ce-branch-header { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: #FFFFFF; border-bottom: 1px solid #E8E5DF; }
            .ce-branch-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
            .ce-branch-body { padding: 12px 14px; }
            .ce-condition-row { display: flex; gap: 8px; margin-bottom: 8px; align-items: center; }
            .ce-add-btn { width: 100%; padding: 10px; border: 1.5px dashed #D6D3D1; border-radius: 8px; background: transparent; color: #78716C; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; }
            .ce-add-btn:hover { border-color: #1C1917; color: #1C1917; }
            .ce-target-row { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #F1F0EB; }
            .ce-btn-outline { padding: 6px 12px; border-radius: 6px; border: 1px solid #E8E5DF; font-size: 13px; font-weight: 500; color: #57534E; background: #FFFFFF; cursor: pointer; display: flex; align-items: center; gap: 4px; }
            .ce-btn-outline:hover { background: #F1F0EB; }
            .ce-btn-primary { padding: 6px 12px; border-radius: 6px; background: #1C1917; color: #F7F6F2; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 4px; }
            .ce-btn-primary:hover { background: #292524; }
            .ce-remove-btn { width: 28px; height: 28px; border-radius: 6px; border: none; background: transparent; color: #A29D96; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
            .ce-remove-btn:hover { background: #FBE7E8; color: #C2363F; }
        `}</style>
        <div className="ce-page">
            {/* Header */}
            <div className="ce-header">
                <div className="ce-header-left">
                    <button onClick={() => navigate(`/forms/${formId}/workflow`)} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100" title="Back to Canvas">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 6, background: '#FCEFDD', color: '#B45309', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <GitBranch className="w-4 h-4" />
                            </div>
                            <h2>Condition: {form?.steps?.find(s => s.step_id === nodeId)?.title || 'Branch'}</h2>
                        </div>
                        <div className="ce-header-sub">
                            {mode === 'switch_case' ? `${cases.length} case${cases.length !== 1 ? 's' : ''} · Switch Case` : `${branches.length} branch${branches.length !== 1 ? 'es' : ''} · If/Else`}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="ce-btn-outline" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="ce-body">
                {/* Branching Mode — now at the top (Issue 5) */}
                <div className="ce-section">
                    <div className="ce-section-title">Branching Mode</div>
                    <div className="ce-mode-toggle">
                        <button
                            className={`ce-mode-btn ${mode === 'switch_case' ? 'active' : ''}`}
                            onClick={() => setMode('switch_case')}
                        >
                            Switch Case
                        </button>
                        <button
                            className={`ce-mode-btn ${mode === 'if_else' ? 'active' : ''}`}
                            onClick={() => setMode('if_else')}
                        >
                            If/Else
                        </button>
                    </div>
                </div>

                {/* From Node — new selector (Issue 5) */}
                <div className="ce-section">
                    <div className="ce-section-title">From Node</div>
                    <label className={labelClass}>Which node's answers to evaluate</label>
                    <select
                        className={inputClass}
                        value={sourceNode}
                        onChange={(e) => handleSourceNodeChange(e.target.value)}
                    >
                        <option value="">— Select a node —</option>
                        {allSteps.map(s => (
                            <option key={s.step_id} value={s.step_id}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Switch Case Mode */}
                {mode === 'switch_case' && (
                    <>
                        {/* Source Field — filtered to From Node's fields (Issue 5) */}
                        <div className="ce-section">
                            <div className="ce-section-title">Source Field</div>
                            <label className={labelClass}>Field to evaluate</label>
                            <select
                                className={inputClass}
                                value={sourceField}
                                onChange={(e) => setSourceField(e.target.value)}
                            >
                                <option value="">— Select a field —</option>
                                {sourceNodeFields.map(f => (
                                    <option key={f.field_id} value={f.field_id}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                            {!sourceNode && (
                                <p style={{ marginTop: 8, fontSize: 12, color: '#A29D96' }}>
                                    Select a From Node first to see its fields.
                                </p>
                            )}
                        </div>

                        <div className="ce-section">
                            <div className="ce-section-title">Cases</div>
                            {cases.map((c, idx) => (
                                <div key={idx} className="ce-case-row">
                                    <div className="ce-case-number">{idx + 1}</div>
                                    <input
                                        className={inputClass}
                                        style={{ flex: '0 0 180px' }}
                                        placeholder="Value to match"
                                        value={c.value}
                                        onChange={(e) => updateCase(idx, 'value', e.target.value)}
                                    />
                                    <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                    <select
                                        className={inputClass}
                                        style={{ flex: 1 }}
                                        value={c.targetStepId}
                                        onChange={(e) => updateCase(idx, 'targetStepId', e.target.value)}
                                    >
                                        <option value="">— Go to node —</option>
                                        {allSteps.map(s => (
                                            <option key={s.step_id} value={s.step_id}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button className="ce-remove-btn" onClick={() => removeCase(idx)} title="Remove case">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}

                            {/* Default case — renamed from "Any other value" to "Default" (Issue 4) */}
                            <div className="ce-case-row default-case">
                                <div className="ce-case-number default-badge">★</div>
                                <input
                                    className={inputClass}
                                    style={{ flex: '0 0 180px', background: 'transparent', border: 'none', fontWeight: 600, color: '#B45309' }}
                                    value="Default"
                                    disabled
                                />
                                <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                <select
                                    className={inputClass}
                                    style={{ flex: 1 }}
                                    value={defaultTarget}
                                    onChange={(e) => setDefaultTarget(e.target.value)}
                                >
                                    <option value="">— Go to node —</option>
                                    {allSteps.map(s => (
                                        <option key={s.step_id} value={s.step_id}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button className="ce-add-btn" onClick={addCase}>
                                <Plus className="w-4 h-4" /> Add Case
                            </button>
                        </div>
                    </>
                )}

                {/* If/Else Mode */}
                {mode === 'if_else' && (
                    <div className="ce-section">
                        <div className="ce-section-title">Branches</div>
                        {branches.map((b, bIdx) => (
                            <div key={bIdx} className="ce-branch">
                                <div className="ce-branch-header">
                                    <div className="ce-branch-dot" style={{ background: ['#6366F1', '#1A7F4E', '#B45309', '#7C3AED', '#C2363F'][bIdx % 5] }} />
                                    <input
                                        className={inputClass}
                                        style={{ flex: 1, border: 'none', fontWeight: 600, fontSize: 14, padding: '4px 8px' }}
                                        value={b.name}
                                        onChange={(e) => updateBranch(bIdx, 'name', e.target.value)}
                                    />
                                    <MatchToggle value={b.matchLogic} onChange={(v) => updateBranch(bIdx, 'matchLogic', v)} />
                                    <button className="ce-remove-btn" onClick={() => removeBranch(bIdx)} title="Remove branch">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="ce-branch-body">
                                    {b.conditions.map((c, cIdx) => (
                                        <div key={cIdx} className="ce-condition-row">
                                            <select
                                                className={inputClass}
                                                style={{ flex: 1 }}
                                                value={c.field}
                                                onChange={(e) => updateCondition(bIdx, cIdx, 'field', e.target.value)}
                                            >
                                                <option value="">— Field —</option>
                                                {sourceNodeFields.map(f => (
                                                    <option key={f.field_id} value={f.field_id}>{f.label}</option>
                                                ))}
                                            </select>
                                            <select
                                                className={inputClass}
                                                style={{ flex: '0 0 130px' }}
                                                value={c.operator}
                                                onChange={(e) => updateCondition(bIdx, cIdx, 'operator', e.target.value)}
                                            >
                                                {OPERATORS.map(op => (
                                                    <option key={op.value} value={op.value}>{op.label}</option>
                                                ))}
                                            </select>
                                            {/* Value input — hidden for unary operators (Issue 6) */}
                                            {!UNARY_OPS.has(c.operator) && (
                                                <input
                                                    className={inputClass}
                                                    style={{ flex: 1 }}
                                                    placeholder="Value"
                                                    value={c.value}
                                                    onChange={(e) => updateCondition(bIdx, cIdx, 'value', e.target.value)}
                                                />
                                            )}
                                            {/* Placeholder to keep layout stable when value is hidden */}
                                            {UNARY_OPS.has(c.operator) && <div style={{ flex: 1 }} />}
                                            <button className="ce-remove-btn" onClick={() => removeCondition(bIdx, cIdx)} title="Remove condition">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button className="ce-add-btn" style={{ marginTop: 8 }} onClick={() => addCondition(bIdx)}>
                                        <Plus className="w-3.5 h-3.5" /> Add Condition
                                    </button>
                                    <div className="ce-target-row">
                                        <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                        <select
                                            className={inputClass}
                                            style={{ flex: 1 }}
                                            value={b.targetStepId}
                                            onChange={(e) => updateBranch(bIdx, 'targetStepId', e.target.value)}
                                        >
                                            <option value="">— Go to node —</option>
                                            {allSteps.map(s => (
                                                <option key={s.step_id} value={s.step_id}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Else (Default) — mirrors the Switch Case default row.
                            The schema (handleSave writes default_target) and evaluator
                            (flowConditions.js reads default_target for if_else) already
                            support this; only the UI was missing. */}
                        <div className="ce-case-row default-case" style={{ marginTop: 12 }}>
                            <div className="ce-case-number default-badge">★</div>
                            <input
                                className={inputClass}
                                style={{ flex: '0 0 180px', background: 'transparent', border: 'none', fontWeight: 600, color: '#B45309' }}
                                value="Else (Default)"
                                disabled
                            />
                            <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <select
                                className={inputClass}
                                style={{ flex: 1 }}
                                value={defaultTarget}
                                onChange={(e) => setDefaultTarget(e.target.value)}
                            >
                                <option value="">— Go to node —</option>
                                {allSteps.map(s => (
                                    <option key={s.step_id} value={s.step_id}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button className="ce-add-btn" onClick={addBranch}>
                            <Plus className="w-4 h-4" /> Add Branch
                        </button>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
